import { useEffect, useState } from 'react';

import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';

import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { useCurrency } from '../../../context/CurrencyContext';

const CATEGORY_OPTIONS = [
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'activity', label: 'Activity' },
    { value: 'meal', label: 'Meal' },
    { value: 'guide', label: 'Guide' },
    { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

const emptyForm = (defaultCurrency = '') => ({
    service_name: '',
    category: '',
    cost_price: '',
    currency: defaultCurrency,
    status: 'draft',
});

const NewPackageComponentModal = ({ open, onClose, onSave, defaultCurrency = '' }) => {
    const { availableCurrencies } = useCurrency();
    const [formData, setFormData] = useState(emptyForm(defaultCurrency));
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData(emptyForm(defaultCurrency));
            setErrors({});
        }
    }, [open, defaultCurrency]);

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const validate = () => {
        const next = {};
        if (!String(formData.service_name || '').trim()) {
            next.service_name = 'Service name is required';
        }
        if (!formData.category) {
            next.category = 'Category is required';
        }
        const cost = parseFloat(formData.cost_price);
        if (formData.cost_price === '' || formData.cost_price === null || Number.isNaN(cost)) {
            next.cost_price = 'Cost price is required';
        } else if (cost < 0) {
            next.cost_price = 'Cost price must be 0 or greater';
        }
        if (!formData.currency) {
            next.currency = 'Currency is required';
        }
        if (!formData.status) {
            next.status = 'Status is required';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave({
                service_name: String(formData.service_name).trim(),
                category: formData.category,
                cost_price: parseFloat(formData.cost_price),
                currency: formData.currency,
                status: formData.status || 'draft',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                <MDTypography variant="h6">Create New Trip Logistics</MDTypography>
                <IconButton onClick={onClose} size="small" disabled={isSaving} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Trip Logistics Name"
                            value={formData.service_name}
                            onChange={handleChange('service_name')}
                            error={Boolean(errors.service_name)}
                            helperText={errors.service_name || ''}
                            inputProps={{ maxLength: 250 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Category"
                            value={formData.category}
                            onChange={handleChange('category')}
                            error={Boolean(errors.category)}
                            helperText={errors.category || ''}
                        >
                            {CATEGORY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Status"
                            value={formData.status}
                            onChange={handleChange('status')}
                            error={Boolean(errors.status)}
                            helperText={errors.status || ''}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Cost Price"
                            value={formData.cost_price}
                            onChange={handleChange('cost_price')}
                            error={Boolean(errors.cost_price)}
                            helperText={errors.cost_price || ''}
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Currency"
                            value={formData.currency}
                            onChange={handleChange('currency')}
                            error={Boolean(errors.currency)}
                            helperText={errors.currency || ''}
                        >
                            {(availableCurrencies || []).map((c) => (
                                <MenuItem key={c.value} value={c.value}>
                                    {c.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isSaving ? 'Creating...' : 'Create Trip Logistics'}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default NewPackageComponentModal;
